import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ArrowRight, 
  Brain, 
  Heart, 
  Compass, 
  BookOpen,
  Loader2,
  Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_alma-metrics/artifacts/usza7456_Alquimia.png";

const SECTION_ICONS = {
  valores: Brain,
  emociones: Heart,
  conductas: Compass,
  narrativa: BookOpen
};

const SECTION_COLORS = {
  valores: "text-coral",
  emociones: "text-lavender",
  conductas: "text-amber-warm",
  narrativa: "text-green-500"
};

const SECTION_BG = {
  valores: "from-coral/10 to-coral/5",
  emociones: "from-lavender/10 to-lavender/5",
  conductas: "from-amber-warm/10 to-amber-warm/5",
  narrativa: "from-green-500/10 to-green-500/5"
};

export default function EvaluationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sections, setSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [valoresSeleccionados, setValoresSeleccionados] = useState([]);

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/questions`);
        setSections(response.data.secciones);
        setLoading(false);
      } catch (error) {
        toast.error("Error al cargar las preguntas");
        console.error(error);
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const currentSection = sections[currentSectionIndex];
  const currentQuestions = currentSection?.preguntas || [];
  const currentQuestion = currentQuestions[currentQuestionIndex];
  
  const totalQuestions = sections.reduce((acc, s) => acc + s.preguntas.length, 0);
  const answeredQuestions = Object.keys(answers).length;
  const progressPercent = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const handleAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Store valores_seleccionados for later
    if (questionId === "valores_1") {
      setValoresSeleccionados(value);
    }
  }, []);

  const canProceed = () => {
    if (!currentQuestion) return false;
    const answer = answers[currentQuestion.id];
    
    if (currentQuestion.opcional) return true;
    if (!answer) return false;
    
    if (currentQuestion.tipo === "multiple_selection") {
      return answer.length > 0;
    }
    if (currentQuestion.tipo === "texto_libre") {
      return currentQuestion.opcional || (answer && answer.trim().length > 0);
    }
    if (currentQuestion.tipo === "escala_valores") {
      return true; // Sliders have default values
    }
    
    return true;
  };

  const goNext = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
    } else {
      handleSubmit();
    }
  };

  const goPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      setCurrentQuestionIndex(sections[currentSectionIndex - 1].preguntas.length - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await axios.post(`${API}/evaluation/submit`, {
        answers: formattedAnswers,
        valores_seleccionados: valoresSeleccionados
      });

      // Store results in sessionStorage for results page
      sessionStorage.setItem("evaluationResults", JSON.stringify(response.data));
      navigate("/resultados");
    } catch (error) {
      toast.error("Error al procesar la evaluación");
      console.error(error);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-cream-alt flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-coral animate-spin" />
      </div>
    );
  }

  const SectionIcon = currentSection ? SECTION_ICONS[currentSection.id] : Brain;
  const sectionColor = currentSection ? SECTION_COLORS[currentSection.id] : "text-coral";
  const sectionBg = currentSection ? SECTION_BG[currentSection.id] : "from-coral/10 to-coral/5";

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream-alt">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-plum/5">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full shadow-soft" />
              <span className="text-plum-light text-sm hidden sm:block">Evaluación de Coherencia</span>
            </div>
            <div className="text-sm text-plum-muted">
              {answeredQuestions} / {totalQuestions} preguntas
            </div>
          </div>
          <div className="relative h-2 bg-plum/10 rounded-full overflow-hidden">
            <motion.div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-coral to-coral-dark rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-32 px-4 min-h-screen">
        <div className="max-w-2xl mx-auto">
          {/* Section Header */}
          <motion.div
            key={currentSection?.id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${sectionBg} flex items-center justify-center shadow-soft`}>
              <SectionIcon className={`w-6 h-6 ${sectionColor}`} />
            </div>
            <div>
              <p className="text-xs text-plum-muted uppercase tracking-wider">
                Sección {currentSectionIndex + 1} de {sections.length}
              </p>
              <h2 className="font-heading text-xl text-plum">{currentSection?.nombre}</h2>
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion?.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="question-card"
            >
              <p className="text-xs text-plum-muted mb-4">
                Pregunta {currentQuestionIndex + 1} de {currentQuestions.length}
              </p>
              
              <h3 className="font-heading text-xl sm:text-2xl text-plum mb-8 leading-relaxed">
                {currentQuestion?.pregunta}
              </h3>

              {/* Question Types */}
              {currentQuestion?.tipo === "multiple_selection" && (
                <MultipleSelectionQuestion 
                  question={currentQuestion}
                  answer={answers[currentQuestion.id] || []}
                  onAnswer={(val) => handleAnswer(currentQuestion.id, val)}
                />
              )}

              {currentQuestion?.tipo === "scenario" && (
                <ScenarioQuestion
                  question={currentQuestion}
                  answer={answers[currentQuestion.id]}
                  onAnswer={(val) => handleAnswer(currentQuestion.id, val)}
                />
              )}

              {currentQuestion?.tipo === "likert" && (
                <LikertQuestion
                  question={currentQuestion}
                  answer={answers[currentQuestion.id]}
                  onAnswer={(val) => handleAnswer(currentQuestion.id, val)}
                />
              )}

              {currentQuestion?.tipo === "texto_libre" && (
                <TextQuestion
                  question={currentQuestion}
                  answer={answers[currentQuestion.id] || ""}
                  onAnswer={(val) => handleAnswer(currentQuestion.id, val)}
                />
              )}

              {currentQuestion?.tipo === "escala_valores" && (
                <ValoresScaleQuestion
                  valores={valoresSeleccionados}
                  answer={answers[currentQuestion.id] || {}}
                  onAnswer={(val) => handleAnswer(currentQuestion.id, val)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 glass-card border-t border-plum/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goPrev}
            disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
            className="text-plum-light hover:text-plum hover:bg-plum/5"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>
          
          <Button
            onClick={goNext}
            disabled={!canProceed() || submitting}
            className="btn-coral px-8"
            data-testid="next-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : currentSectionIndex === sections.length - 1 && currentQuestionIndex === currentQuestions.length - 1 ? (
              <>
                Ver Resultados
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
}

// Question Components
function MultipleSelectionQuestion({ question, answer, onAnswer }) {
  const toggleOption = (option) => {
    const currentAnswer = answer || [];
    if (currentAnswer.includes(option)) {
      onAnswer(currentAnswer.filter(o => o !== option));
    } else if (currentAnswer.length < (question.maxSeleccion || 99)) {
      onAnswer([...currentAnswer, option]);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-plum-muted mb-4">
        Selecciona hasta {question.maxSeleccion} opciones
      </p>
      <div className="grid gap-3">
        {question.opciones.map((option, i) => {
          const isSelected = (answer || []).includes(option);
          return (
            <button
              key={i}
              onClick={() => toggleOption(option)}
              className={`option-btn flex items-center justify-between ${isSelected ? "selected" : ""}`}
              data-testid={`option-${i}`}
            >
              <span className={isSelected ? "text-coral font-medium" : ""}>{option}</span>
              {isSelected && <Check className="w-5 h-5 text-coral" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScenarioQuestion({ question, answer, onAnswer }) {
  return (
    <div className="space-y-3">
      {question.opciones.map((option, i) => {
        const isSelected = answer?.valor === option.valor;
        return (
          <button
            key={i}
            onClick={() => onAnswer(option)}
            className={`option-btn ${isSelected ? "selected" : ""}`}
            data-testid={`scenario-option-${i}`}
          >
            <span className={isSelected ? "text-coral font-medium" : ""}>{option.texto}</span>
          </button>
        );
      })}
    </div>
  );
}

function LikertQuestion({ question, answer, onAnswer }) {
  return (
    <div className="space-y-3">
      {question.opciones.map((option, i) => {
        const isSelected = answer?.valor === option.valor;
        return (
          <button
            key={i}
            onClick={() => onAnswer(option)}
            className={`option-btn ${isSelected ? "selected" : ""}`}
            data-testid={`likert-option-${i}`}
          >
            <span className={isSelected ? "text-coral font-medium" : ""}>{option.texto}</span>
          </button>
        );
      })}
    </div>
  );
}

function TextQuestion({ question, answer, onAnswer }) {
  return (
    <div>
      <Textarea
        value={answer}
        onChange={(e) => onAnswer(e.target.value)}
        placeholder={question.placeholder}
        className="min-h-[120px] bg-white/50 border-plum/10 text-plum placeholder:text-plum/30 resize-none focus:ring-coral/30 focus:border-coral/50"
        data-testid="text-input"
      />
      {question.opcional && (
        <p className="text-xs text-plum-muted mt-2">Esta pregunta es opcional</p>
      )}
    </div>
  );
}

function ValoresScaleQuestion({ valores, answer, onAnswer }) {
  const handleSliderChange = (valor, newValue) => {
    onAnswer({
      ...answer,
      valores_scores: {
        ...(answer.valores_scores || {}),
        [valor]: newValue[0]
      }
    });
  };

  if (!valores || valores.length === 0) {
    return (
      <div className="text-plum-muted text-center py-8">
        Primero selecciona tus valores en la pregunta anterior.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <p className="text-sm text-plum-muted mb-6">
        Califica del 1 al 10 qué tanto vives cada valor
      </p>
      {valores.map((valor, i) => {
        const currentValue = answer.valores_scores?.[valor] || 5;
        return (
          <div key={i} className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-plum font-medium">{valor}</span>
              <span className="text-coral font-bold text-lg">{currentValue}</span>
            </div>
            <Slider
              value={[currentValue]}
              onValueChange={(val) => handleSliderChange(valor, val)}
              min={1}
              max={10}
              step={1}
              className="w-full"
              data-testid={`slider-${i}`}
            />
            <div className="flex justify-between text-xs text-plum-muted">
              <span>Poco</span>
              <span>Mucho</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
